'use client';

import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { useConsent } from '@/lib/consent-client';

/**
 * PostHog provider consent-gated.
 *
 * - Carga posthog-js solo si `consent.analytics === true` y `posthogKey` existe.
 * - Session replay y heatmaps solo si `consent.preferences === true`.
 * - Si el user revoca consent → `opt_out_capturing()` detiene captura.
 * - Si re-acepta → `opt_in_capturing()` reanuda.
 * - `person_profiles: 'identified_only'` → no crea profile para usuarios anónimos
 *   (cumple con LFPDPPP mínimo invasivo).
 *
 * NO usa SDK server-side aquí; para eventos críticos server-side (Purchase,
 * order status) se usará una implementación separada vía fetch a /capture.
 */
export function PostHogProvider({
  posthogKey,
  posthogHost,
}: {
  posthogKey: string | undefined;
  posthogHost: string;
}) {
  const { consent, has } = useConsent();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!posthogKey) return;

    const analyticsAllowed = has('analytics');
    const replayAllowed = has('preferences');

    if (!analyticsAllowed) {
      // User no autorizó analytics. Si antes estaba init, desactivar.
      if (initializedRef.current && typeof posthog.opt_out_capturing === 'function') {
        posthog.opt_out_capturing();
      }
      return;
    }

    // Primera inicialización tras aceptar consent
    if (!initializedRef.current) {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        defaults: '2026-01-30',
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        disable_session_recording: !replayAllowed,
        opt_out_capturing_by_default: false,
        // Compliance: no capturar GET params sensibles en URLs
        sanitize_properties: (properties) => {
          if (properties['$current_url'] && typeof properties['$current_url'] === 'string') {
            try {
              const url = new URL(properties['$current_url']);
              // Limpiar query params que puedan contener tokens
              ['token', 'confirm', 'u', 'email', 'auth_token'].forEach((k) =>
                url.searchParams.delete(k)
              );
              properties['$current_url'] = url.toString();
            } catch {
              /* noop */
            }
          }
          return properties;
        },
      });
      initializedRef.current = true;
    } else {
      // Re-aceptación
      if (typeof posthog.opt_in_capturing === 'function') {
        posthog.opt_in_capturing();
      }
    }

    // Gating de session replay independiente del consent analytics
    if (initializedRef.current) {
      if (replayAllowed) {
        if (typeof posthog.startSessionRecording === 'function') {
          posthog.startSessionRecording();
        }
      } else {
        if (typeof posthog.stopSessionRecording === 'function') {
          posthog.stopSessionRecording();
        }
      }
    }
  }, [posthogKey, posthogHost, consent.analytics, consent.preferences, has]);

  return null;
}
